import { useState, useEffect, useRef, useCallback } from 'react';
import { recordWin } from './gameUtils';

const SEGMENT_COLORS = ['#0d9488', '#1a2744', '#14b8a6', '#243559', '#0f766e', '#1e3a5f', '#2dd4bf', '#1a2744', '#0d9488', '#2c4a6e'];

const SpinWheel = ({ clientId, prizes: rawPrizes, setVoucher, setScreen }) => {
  const canvasRef = useRef(null);
  const rotationRef = useRef(0);
  const spinningRef = useRef(false);
  const [spinning, setSpinning] = useState(false);
  const [showLoseDialog, setShowLoseDialog] = useState(false);
  const [segments, setSegments] = useState([]);

  useEffect(() => {
    const active = rawPrizes.filter(p => p.active !== false);
    if (active.length === 0) return;
    const segs = [];
    const losePrize = active.find(p => !p.win);
    const winPrizes = active.filter(p => p.win);
    const half = losePrize ? losePrize.weight / 2 : 0;
    let insertedLose = 0;
    winPrizes.forEach((p, i) => {
      segs.push({ ...p, color: SEGMENT_COLORS[segs.length % SEGMENT_COLORS.length] });
      if (insertedLose < 2 && i % 2 === 0 && losePrize) {
        segs.push({ ...losePrize, id: `LOSE${insertedLose + 1}`, weight: half, color: insertedLose === 0 ? '#243559' : '#1e3a5f' });
        insertedLose++;
      }
    });
    while (insertedLose < 2 && losePrize) {
      segs.push({ ...losePrize, id: `LOSE${insertedLose + 1}`, weight: half, color: insertedLose === 0 ? '#162238' : '#1a2d47' });
      insertedLose++;
    }
    setSegments(segs);
  }, [rawPrizes]);

  const drawWheel = useCallback((rotation) => {
    const canvas = canvasRef.current;
    if (!canvas || segments.length === 0) return;
    const ctx = canvas.getContext('2d');
    const size = canvas.width;
    const cx = size / 2, cy = size / 2;
    const radius = cx - 12;
    const tw = segments.reduce((s, p) => s + (p.weight || 0), 0);

    ctx.clearRect(0, 0, size, size);
    ctx.beginPath();
    ctx.arc(cx, cy, radius + 6, 0, Math.PI * 2);
    ctx.strokeStyle = '#0d9488';
    ctx.lineWidth = 3;
    ctx.stroke();

    let angle = -Math.PI / 2 + rotation;
    for (const seg of segments) {
      const sliceAngle = (seg.weight / tw) * Math.PI * 2;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, radius, angle, angle + sliceAngle);
      ctx.closePath();
      ctx.fillStyle = seg.color || '#0d9488';
      ctx.fill();
      ctx.strokeStyle = '#0d948833';
      ctx.lineWidth = 1.5;
      ctx.stroke();

      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(angle + sliceAngle / 2);
      ctx.fillStyle = '#FDF8F0';
      ctx.font = `bold ${size < 350 ? 10 : 12}px Manrope, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const textR = radius * 0.65;
      const label = seg.label.length > 16 ? seg.label.slice(0, 15) + '.' : seg.label;
      ctx.fillText(label, textR, 0);
      ctx.restore();
      angle += sliceAngle;
    }

    ctx.beginPath();
    ctx.arc(cx, cy, 30, 0, Math.PI * 2);
    ctx.fillStyle = '#1a2744';
    ctx.fill();
    ctx.strokeStyle = '#0d9488';
    ctx.lineWidth = 2;
    ctx.stroke();

    const ps = 18;
    ctx.beginPath();
    ctx.moveTo(cx, 4);
    ctx.lineTo(cx - ps / 2, ps + 4);
    ctx.lineTo(cx + ps / 2, ps + 4);
    ctx.closePath();
    ctx.fillStyle = '#0d9488';
    ctx.fill();
  }, [segments]);

  useEffect(() => { drawWheel(0); }, [drawWheel]);

  const spin = () => {
    if (spinningRef.current || segments.length === 0) return;
    spinningRef.current = true;
    setSpinning(true);

    const tw = segments.reduce((s, p) => s + (p.weight || 0), 0);
    const r = Math.random() * tw;
    let cum = 0, winIdx = segments.length - 1;
    for (let i = 0; i < segments.length; i++) {
      cum += segments[i].weight;
      if (r < cum) { winIdx = i; break; }
    }
    const prize = segments[winIdx];

    let cumAngle = 0;
    for (let i = 0; i < winIdx; i++) cumAngle += (segments[i].weight / tw) * Math.PI * 2;
    const sliceAngle = (prize.weight / tw) * Math.PI * 2;
    const targetInSlice = cumAngle + sliceAngle * (0.3 + Math.random() * 0.4);
    const baseRot = (Math.PI * 2 - targetInSlice) % (Math.PI * 2);
    const fullSpins = (5 + Math.floor(Math.random() * 3)) * Math.PI * 2;
    const totalRot = fullSpins + baseRot;

    const duration = 5500;
    const startTime = performance.now();
    const startRot = rotationRef.current;

    const animate = (now) => {
      const elapsed = now - startTime;
      const t = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - t, 4);
      const currentRot = startRot + totalRot * eased;
      rotationRef.current = currentRot;
      drawWheel(currentRot);
      if (t < 1) requestAnimationFrame(animate);
      else {
        spinningRef.current = false;
        setSpinning(false);
        handleResult(prize);
      }
    };
    requestAnimationFrame(animate);
  };

  const handleResult = async (prize) => {
    if (!prize.win) {
      // Record the loss in backend so they can't replay today
      try {
        await recordWin(clientId, prize.id.replace(/\d/g, ''), prize.label);
      } catch {}
      setShowLoseDialog(true);
      return;
    }
    const prizeId = prize.id.replace(/\d/g, '');
    const data = await recordWin(clientId, prizeId, prize.label);
    if (data.win && data.voucher) {
      setVoucher(data.voucher);
      setScreen('voucher');
    }
  };

  const retryOrQuit = (retry) => {
    setShowLoseDialog(false);
    setScreen('register');
  };

  const canvasSize = Math.min(380, typeof window !== 'undefined' ? window.innerWidth - 48 : 380);

  return (
    <div className="text-center relative" data-testid="wheel-screen">
      <h2 className="text-2xl font-bold text-[#0d9488] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
        Tournez la roue !
      </h2>
      <div className="relative inline-block">
        <canvas ref={canvasRef} width={canvasSize} height={canvasSize} className="mx-auto" data-testid="wheel-canvas" />
        <button onClick={spin} disabled={spinning}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-[#0d9488] text-white font-bold text-[10px] tracking-wider hover:bg-[#14b8a6] transition-all disabled:opacity-50 z-10"
          style={{ fontFamily: "'Manrope', sans-serif" }} data-testid="spin-button">
          {spinning ? '...' : 'LANCER'}
        </button>
      </div>
      {showLoseDialog && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4" data-testid="lose-dialog">
          <div className="bg-[#1a2744] border border-[#0d9488]/20 rounded-2xl p-8 max-w-sm w-full text-center">
            <p className="text-3xl mb-3">&#128532;</p>
            <h3 className="text-xl text-white font-semibold mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Pas de chance !
            </h3>
            <p className="text-white/60 text-sm mb-6" style={{ fontFamily: "'Manrope', sans-serif" }}>Merci d'avoir participe ! Revenez demain pour retenter votre chance.</p>
            <div className="flex gap-3">
              <button onClick={() => retryOrQuit(false)}
                className="flex-1 py-2.5 rounded-lg bg-[#0d9488] text-white font-bold text-sm hover:bg-[#14b8a6] transition"
                style={{ fontFamily: "'Manrope', sans-serif" }}
                data-testid="lose-quit">Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpinWheel;
